import { useState } from "react";
import { useLocaleState, useTranslate } from "ra-core";

import { useIsMobile } from "@/hooks/use-mobile";
import type { Identifier } from "ra-core";

import type { ContentPiece } from "../types";
import {
  FORMAT_ICONS,
  STATUS_COLORS,
  buildMonthGrid,
  toDateKey,
} from "./pieceModel";

export const MonthGrid = ({
  month,
  pieces,
  selectedPieceId,
  clientNames,
  onSelect,
}: {
  month: Date;
  pieces: ContentPiece[];
  selectedPieceId: string | null;
  /** When set (all-clients view), each chip is prefixed with the client name. */
  clientNames?: Map<string, string>;
  onSelect: (pieceId: Identifier) => void;
}) => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const weekdays = translate("resources.content_pieces.weekdays").split(",");
  const todayKey = toDateKey(new Date());

  const piecesByDay = new Map<string, ContentPiece[]>();
  pieces.forEach((piece) => {
    if (!piece.publish_date) return;
    const key = piece.publish_date.slice(0, 10);
    piecesByDay.set(key, [...(piecesByDay.get(key) ?? []), piece]);
  });

  if (isMobile) {
    return (
      <MonthAgenda
        month={month}
        piecesByDay={piecesByDay}
        selectedPieceId={selectedPieceId}
        clientNames={clientNames}
        onSelect={onSelect}
        todayKey={todayKey}
      />
    );
  }

  return (
    <section className="grid grid-cols-7 gap-1.5 rounded-[22px] bg-card p-3.5 auto-rows-[minmax(108px,auto)] [grid-template-rows:28px]">
      {weekdays.map((day) => (
        <div
          key={day}
          className="px-2 pt-1 text-xs font-medium text-muted-foreground/70"
        >
          {day}
        </div>
      ))}
      {buildMonthGrid(month).map(({ date, inMonth }) => {
        const key = toDateKey(date);
        const dayPieces = inMonth ? (piecesByDay.get(key) ?? []) : [];
        const isToday = key === todayKey;
        return (
          <div
            key={key}
            className="flex min-w-0 flex-col gap-1 rounded-[14px] p-1.5 hover:bg-accent/60"
          >
            <span
              className={`grid size-6 place-items-center rounded-full text-xs font-medium ${
                isToday
                  ? "bg-primary text-primary-foreground"
                  : inMonth
                    ? "text-muted-foreground"
                    : "text-muted-foreground/30"
              }`}
            >
              {date.getDate()}
            </span>
            {dayPieces.map((piece) => {
              const Icon = FORMAT_ICONS[piece.format];
              const prefix = clientNames?.get(String(piece.company_id));
              return (
                <button
                  key={piece.id}
                  type="button"
                  onClick={() => onSelect(piece.id)}
                  title={piece.title}
                  className={`flex min-w-0 items-center gap-1.5 rounded-[9px] border-l-[3px] bg-secondary px-2 py-1.5 text-left text-xs transition-colors hover:bg-accent ${
                    String(piece.id) === selectedPieceId
                      ? "ring-1 ring-primary"
                      : ""
                  }`}
                  style={{ borderLeftColor: STATUS_COLORS[piece.status] }}
                >
                  <Icon className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate">
                    {prefix ? (
                      <span className="text-muted-foreground">{prefix} · </span>
                    ) : null}
                    {piece.title}
                  </span>
                </button>
              );
            })}
          </div>
        );
      })}
    </section>
  );
};

// Phone layout: the full month as a compact grid with status dots, and the
// pieces of the tapped day listed underneath.
const MonthAgenda = ({
  month,
  piecesByDay,
  selectedPieceId,
  clientNames,
  onSelect,
  todayKey,
}: {
  month: Date;
  piecesByDay: Map<string, ContentPiece[]>;
  selectedPieceId: string | null;
  clientNames?: Map<string, string>;
  onSelect: (pieceId: Identifier) => void;
  todayKey: string;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const weekdays = translate("resources.content_pieces.weekdays").split(",");
  const cells = buildMonthGrid(month);
  const monthKeys = cells
    .filter(({ inMonth }) => inMonth)
    .map(({ date }) => toDateKey(date));
  const defaultDay = monthKeys.includes(todayKey)
    ? todayKey
    : (monthKeys.find((key) => piecesByDay.has(key)) ?? monthKeys[0]);
  const [pickedDay, setPickedDay] = useState<string | null>(null);
  const selectedDay =
    pickedDay && monthKeys.includes(pickedDay) ? pickedDay : defaultDay;
  const dayPieces = piecesByDay.get(selectedDay) ?? [];
  const [year, monthIndex, day] = selectedDay.split("-").map(Number);

  return (
    <section className="flex flex-col gap-3">
      <div className="rounded-[22px] bg-card p-3">
        <div className="grid grid-cols-7 gap-1">
          {weekdays.map((weekday) => (
            <div
              key={weekday}
              className="pb-1 text-center text-[11px] font-medium text-muted-foreground/70"
            >
              {weekday.slice(0, 1)}
            </div>
          ))}
          {cells.map(({ date, inMonth }) => {
            const key = toDateKey(date);
            const pieces = inMonth ? (piecesByDay.get(key) ?? []) : [];
            const isSelected = inMonth && key === selectedDay;
            const isToday = key === todayKey;
            return (
              <button
                key={key}
                type="button"
                disabled={!inMonth}
                onClick={() => setPickedDay(key)}
                className={`flex h-12 flex-col items-center justify-center gap-1 rounded-xl text-sm transition-colors ${
                  isSelected
                    ? "bg-primary text-primary-foreground"
                    : inMonth
                      ? "hover:bg-accent"
                      : "text-muted-foreground/30"
                } ${isToday && !isSelected ? "font-bold text-primary" : ""}`}
              >
                <span>{date.getDate()}</span>
                <span className="flex h-1.5 gap-0.5">
                  {pieces.slice(0, 3).map((piece) => (
                    <span
                      key={piece.id}
                      className="size-1.5 rounded-full"
                      style={{
                        background: isSelected
                          ? "#fff"
                          : STATUS_COLORS[piece.status],
                      }}
                    />
                  ))}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-[22px] bg-card p-4">
        <div className="mb-2 text-sm font-semibold text-muted-foreground first-letter:uppercase">
          {new Date(year, monthIndex - 1, day).toLocaleDateString(
            locale === "es" ? "es-CO" : locale,
            { weekday: "long", day: "numeric", month: "long" },
          )}
        </div>
        {dayPieces.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {translate("resources.content_pieces.no_pieces_day")}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {dayPieces.map((piece) => {
              const Icon = FORMAT_ICONS[piece.format];
              const prefix = clientNames?.get(String(piece.company_id));
              return (
                <button
                  key={piece.id}
                  type="button"
                  onClick={() => onSelect(piece.id)}
                  className={`flex items-center gap-2.5 rounded-xl border-l-[3px] bg-secondary px-3 py-2.5 text-left text-sm ${
                    String(piece.id) === selectedPieceId
                      ? "ring-1 ring-primary"
                      : ""
                  }`}
                  style={{ borderLeftColor: STATUS_COLORS[piece.status] }}
                >
                  <Icon className="size-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">
                      {piece.title}
                    </span>
                    {prefix ? (
                      <span className="block truncate text-xs text-muted-foreground">
                        {prefix}
                      </span>
                    ) : null}
                  </span>
                  <span
                    className="shrink-0 text-xs font-semibold"
                    style={{ color: STATUS_COLORS[piece.status] }}
                  >
                    {translate(
                      `resources.content_pieces.statuses.${piece.status}`,
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
